import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';

/**
 * ServiceTimeline - Visualizes parliamentary service across chambers
 *
 * Props:
 * - serviceRecords: Array of service records with DateStart1, DateEnd1, and chamber info
 * - overallStart: Earliest date of service
 * - overallEnd: Latest date of service (or current date if still serving)
 * - mpOrSenator: Array indicating roles ['Member', 'Senator']
 */
export default function ServiceTimeline({
  serviceRecords = [],
  overallStart,
  overallEnd,
  mpOrSenator = [],
  electorateService = [],
  ministryRecords = [],
  shadowMinistryRecords = [],
  partyColors = {}
}) {

  // Colors for different service types
  const COLORS = {
    house: '#2e7d32',      // Green for House of Representatives
    senate: '#c62828',      // Red for Senate
    ministry: '#1565c0',    // Blue for Ministry
    shadow: '#7b1fa2',      // Purple for Shadow Ministry
    party: '#ff6f00',       // Orange for Party (fallback)
    gap: '#e0e0e0'          // Grey for gaps
  };

  // Parse date to timestamp
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).getTime();
  };

  // Get current date for ongoing service
  const now = new Date().getTime();

  // Determine timeline bounds
  const timelineStart = parseDate(overallStart) || now;
  const timelineEnd = parseDate(overallEnd) || now;
  const totalDuration = timelineEnd - timelineStart;

  if (totalDuration <= 0) return null;

  // Build service periods from electorate service (for House members)
  const housePeriods = (electorateService || [])
    .map(es => ({
      start: parseDate(es.ServiceStart),
      end: parseDate(es.ServiceEnd) || now,
      label: es.Electorate,
      state: es.State,
      type: 'house'
    }))
    .filter(p => p.start);

  // Build senate periods from records of service
  // ROSTypeID 9 = Parliamentary Service, MpOrSenator field indicates chamber
  const senatePeriods = (serviceRecords || [])
    .filter(r => r.ROSTypeID === 9 && r.MpOrSenator === 'Senator')
    .map(r => ({
      start: parseDate(r.DateStart1),
      end: parseDate(r.DateEnd1) || now,
      label: `Senator for ${r.Value1}`,
      state: r.Value1,
      type: 'senate'
    }))
    .filter(p => p.start);

  // Build House periods from Parliamentary Service records
  // Use MpOrSenator field to identify House service
  const houseServicePeriods = (serviceRecords || [])
    .filter(r => r.ROSTypeID === 9 && r.MpOrSenator === 'Member')
    .map(r => ({
      start: parseDate(r.DateStart1),
      end: parseDate(r.DateEnd1) || now,
      label: r.Value2 ? `Member for ${r.Value2}` : `Member (${r.Value1})`,
      state: r.Value1,
      electorate: r.Value2,
      type: 'house'
    }))
    .filter(p => p.start);

  // Use electorate service for House periods if available, otherwise use service records
  let memberPeriods = housePeriods.length > 0 ? housePeriods : houseServicePeriods;

  // For dual-chamber members, ensure we have House periods from service records if electorateService is empty
  const isDualChamber = mpOrSenator?.includes('Member') && mpOrSenator?.includes('Senator');
  if (isDualChamber && housePeriods.length === 0 && houseServicePeriods.length > 0) {
    memberPeriods = houseServicePeriods;
  }

  // Ministry periods - build proper title with "Minister for..." prefix
  const ministryPeriods = (ministryRecords || [])
    .map(r => {
      // Build proper position string with role prefix
      let label = r.Role || 'Minister';
      if (r.Entity) {
        // Add "for" or custom preposition before the entity (portfolio)
        const prep = r.Prep || 'for';
        label = `${label} ${prep} ${r.Entity}`;
      } else if (r.Position) {
        label = r.Position;
      }
      return {
        start: parseDate(r.RDateStart),
        end: parseDate(r.RDateEnd) || now,
        label,
        role: r.Role,
        entity: r.Entity,
        ministry: r.Ministry,
        type: 'ministry'
      };
    })
    .filter(p => p.start);

  // Shadow ministry periods - build proper title with "Shadow Minister for..." prefix
  const shadowPeriods = (shadowMinistryRecords || [])
    .map(r => {
      // Build proper position string with role prefix
      let label = r.Role || 'Shadow Minister';
      if (r.Entity) {
        const prep = r.Prep || 'for';
        label = `${label} ${prep} ${r.Entity}`;
      } else if (r.Position) {
        label = r.Position;
      }
      return {
        start: parseDate(r.RDateStart),
        end: parseDate(r.RDateEnd) || now,
        label,
        role: r.Role,
        entity: r.Entity,
        ministry: r.Ministry,
        type: 'shadow'
      };
    })
    .filter(p => p.start);

  // Party periods from Records of Service (ROSTypeID 11 = Parties Represented)
  const partyPeriods = (serviceRecords || [])
    .filter(r => r.ROSTypeID === 11)
    .map(r => {
      const startDate = parseDate(r.DateStart1);
      let endDate = parseDate(r.DateEnd1);
      // 1900-01-01 is used as a placeholder for "current/ongoing"
      if (r.DateEnd1 === '1900-01-01' || !endDate || endDate < startDate) {
        endDate = now;
      }
      // Skip records with placeholder start dates
      if (r.DateStart1 === '1900-01-01' || !startDate) {
        return null;
      }
      // Look up party color from partyColors prop (by name or abbreviation)
      const partyColor = partyColors[r.Value1] || partyColors[r.ValueAbbrev1] || COLORS.party;
      return {
        start: startDate,
        end: endDate,
        label: r.Value1,
        abbrev: r.ValueAbbrev1,
        type: 'party',
        color: partyColor
      };
    })
    .filter(p => p && p.start)
    .sort((a, b) => a.start - b.start);

  // Combine all chamber periods and sort by start date
  const allChamberPeriods = [...memberPeriods, ...senatePeriods].sort((a, b) => a.start - b.start);

  // Calculate position as percentage
  const getPosition = (timestamp) => {
    return ((timestamp - timelineStart) / totalDuration) * 100;
  };

  // Format date for display
  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-AU', {
      month: 'short',
      year: 'numeric'
    });
  };

  // Generate year markers
  const startYear = new Date(timelineStart).getFullYear();
  const endYear = new Date(timelineEnd).getFullYear();
  const yearMarkers = [];

  // Determine step size based on duration
  const yearSpan = endYear - startYear;
  const step = yearSpan > 40 ? 10 : yearSpan > 20 ? 5 : yearSpan > 10 ? 2 : 1;

  for (let year = Math.ceil(startYear / step) * step; year <= endYear; year += step) {
    const timestamp = new Date(year, 0, 1).getTime();
    if (timestamp >= timelineStart && timestamp <= timelineEnd) {
      yearMarkers.push({ year, position: getPosition(timestamp) });
    }
  }

  // Find gaps in service
  const gaps = [];
  for (let i = 0; i < allChamberPeriods.length - 1; i++) {
    const currentEnd = allChamberPeriods[i].end;
    const nextStart = allChamberPeriods[i + 1].start;
    // Only show gaps larger than 30 days
    if (nextStart - currentEnd > 30 * 24 * 60 * 60 * 1000) {
      gaps.push({
        start: currentEnd,
        end: nextStart,
        type: 'gap'
      });
    }
  }

  // Render a period bar
  const renderPeriod = (period, index, row = 0) => {
    const left = getPosition(period.start);
    const width = getPosition(period.end) - left;
    // Use period-specific color if available (for parties), otherwise use type color
    const color = period.color || COLORS[period.type];

    // Build tooltip content based on period type
    const renderTooltipContent = () => {
      if (period.type === 'ministry' || period.type === 'shadow') {
        return (
          <Box sx={{ color: '#fff', minWidth: 200 }}>
            {/* Role chip */}
            <Box sx={{
              display: 'inline-block',
              bgcolor: 'rgba(255,255,255,0.2)',
              px: 1,
              py: 0.25,
              borderRadius: 1,
              mb: 0.5,
              fontSize: '0.7rem'
            }}>
              {period.role || (period.type === 'shadow' ? 'Shadow Minister' : 'Minister')}
            </Box>
            {/* Portfolio */}
            {period.entity && (
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'inherit', mb: 0.5 }}>
                {period.entity}
              </Typography>
            )}
            {!period.entity && (
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'inherit', mb: 0.5 }}>
                {period.label}
              </Typography>
            )}
            {/* Ministry name */}
            {period.ministry && (
              <Typography variant="caption" display="block" sx={{ color: 'inherit', opacity: 0.9 }}>
                {period.ministry}
              </Typography>
            )}
            {/* Dates */}
            <Typography variant="caption" display="block" sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.2)', color: 'inherit' }}>
              {formatDate(period.start)} — {period.end >= now - 86400000 ? 'Present' : formatDate(period.end)}
            </Typography>
          </Box>
        );
      }

      // Default tooltip for chamber/party periods
      return (
        <Box sx={{ color: '#fff' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'inherit' }}>
            {period.label}
          </Typography>
          {period.state && (
            <Typography variant="caption" display="block" sx={{ color: 'inherit' }}>
              {period.state}
            </Typography>
          )}
          {period.ministry && (
            <Typography variant="caption" display="block" sx={{ color: 'inherit' }}>
              {period.ministry}
            </Typography>
          )}
          {period.abbrev && (
            <Typography variant="caption" display="block" sx={{ color: 'inherit' }}>
              ({period.abbrev})
            </Typography>
          )}
          <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.9, color: 'inherit' }}>
            {formatDate(period.start)} — {period.end >= now - 86400000 ? 'Present' : formatDate(period.end)}
          </Typography>
        </Box>
      );
    };

    return (
      <Tooltip
        key={`${period.type}-${index}`}
        title={renderTooltipContent()}
        arrow
        placement="top"
      >
        <Box
          sx={{
            position: 'absolute',
            left: `${left}%`,
            width: `${Math.max(width, 0.5)}%`,
            height: '100%',
            bgcolor: color,
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'scaleY(1.2)',
              zIndex: 10,
              boxShadow: `0 2px 8px ${color}88`
            }
          }}
        />
      </Tooltip>
    );
  };

  // Check if we have any data to show
  const hasData = allChamberPeriods.length > 0 || ministryPeriods.length > 0 || shadowPeriods.length > 0 || partyPeriods.length > 0;

  if (!hasData) {
    return null;
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
        {(memberPeriods.length > 0 || mpOrSenator?.includes('Member')) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: COLORS.house, borderRadius: '3px' }} />
            <Typography variant="body2" color="text.secondary">House of Representatives</Typography>
          </Box>
        )}
        {(senatePeriods.length > 0 || mpOrSenator?.includes('Senator')) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: COLORS.senate, borderRadius: '3px' }} />
            <Typography variant="body2" color="text.secondary">Senate</Typography>
          </Box>
        )}
        {ministryPeriods.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: COLORS.ministry, borderRadius: '3px' }} />
            <Typography variant="body2" color="text.secondary">Ministry</Typography>
          </Box>
        )}
        {shadowPeriods.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: COLORS.shadow, borderRadius: '3px' }} />
            <Typography variant="body2" color="text.secondary">Shadow Ministry</Typography>
          </Box>
        )}
        {partyPeriods.length > 0 && (
          <>
            {/* Show each unique party with its color */}
            {[...new Map(partyPeriods.map(p => [p.label, p])).values()].map((party, idx) => (
              <Box key={`party-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 16, bgcolor: party.color, borderRadius: '3px' }} />
                <Typography variant="body2" color="text.secondary">
                  {party.abbrev || party.label}
                </Typography>
              </Box>
            ))}
          </>
        )}
        {gaps.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
              width: 16,
              height: 16,
              bgcolor: COLORS.gap,
              borderRadius: '3px',
              border: '1px dashed #999'
            }} />
            <Typography variant="body2" color="text.secondary">Gap in service</Typography>
          </Box>
        )}
      </Box>

      {/* Timeline container */}
      <Box sx={{
        position: 'relative',
        bgcolor: '#f5f5f5',
        borderRadius: 2,
        p: 2,
        pt: 1
      }}>
        {/* Year markers */}
        <Box sx={{ position: 'relative', height: 20, mb: 1 }}>
          {yearMarkers.map(({ year, position }) => (
            <Typography
              key={year}
              variant="caption"
              sx={{
                position: 'absolute',
                left: `${position}%`,
                transform: 'translateX(-50%)',
                color: 'text.secondary',
                fontSize: '0.7rem'
              }}
            >
              {year}
            </Typography>
          ))}
        </Box>

        {/* Chamber service row */}
        {allChamberPeriods.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Parliamentary Service
            </Typography>
            <Box sx={{
              position: 'relative',
              height: 24,
              bgcolor: '#e0e0e0',
              borderRadius: '4px'
            }}>
              {/* Render gaps first (behind) */}
              {gaps.map((gap, i) => {
                const left = getPosition(gap.start);
                const width = getPosition(gap.end) - left;
                return (
                  <Tooltip
                    key={`gap-${i}`}
                    title={
                      <Typography variant="caption">
                        Gap in service: {formatDate(gap.start)} — {formatDate(gap.end)}
                      </Typography>
                    }
                    arrow
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        left: `${left}%`,
                        width: `${width}%`,
                        height: '100%',
                        bgcolor: COLORS.gap,
                        backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(0,0,0,0.05) 5px, rgba(0,0,0,0.05) 10px)',
                        borderRadius: '4px',
                        border: '1px dashed #bbb'
                      }}
                    />
                  </Tooltip>
                );
              })}
              {/* Render service periods */}
              {allChamberPeriods.map((period, i) => renderPeriod(period, i))}
            </Box>
          </Box>
        )}

        {/* Ministry row */}
        {ministryPeriods.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Ministry Appointments
            </Typography>
            <Box sx={{
              position: 'relative',
              height: 20,
              bgcolor: '#e3f2fd',
              borderRadius: '4px'
            }}>
              {ministryPeriods.map((period, i) => renderPeriod(period, i))}
            </Box>
          </Box>
        )}

        {/* Shadow Ministry row */}
        {shadowPeriods.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Shadow Ministry Appointments
            </Typography>
            <Box sx={{
              position: 'relative',
              height: 20,
              bgcolor: '#f3e5f5',
              borderRadius: '4px'
            }}>
              {shadowPeriods.map((period, i) => renderPeriod(period, i))}
            </Box>
          </Box>
        )}

        {/* Party Membership row */}
        {partyPeriods.length > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Party Membership
            </Typography>
            <Box sx={{
              position: 'relative',
              height: 20,
              bgcolor: '#fff3e0',
              borderRadius: '4px'
            }}>
              {partyPeriods.map((period, i) => renderPeriod(period, i))}
            </Box>
          </Box>
        )}

      </Box>
    </Box>
  );
}
